import L from "leaflet";
import React, { useEffect } from "react";
import { Circle, Marker, Polygon, useMap } from "react-leaflet";
import type { GeographicAreas } from "./IconsController";

const GeographicAreasLayer: React.FC<{
  geographicAreas: GeographicAreas | undefined;
  showRefPointsNames: boolean;
  onToggleNames?: (show: boolean) => void;
}> = React.memo(({ geographicAreas, showRefPointsNames, onToggleNames }) => {
  const map = useMap();

  // Mostra os nomes sozinho a partir do zoom 15, mas só enquanto o usuário não
  // mexeu no checkbox: a partir daí o IconsController para de passar
  // onToggleNames e a escolha dele manda.
  useEffect(() => {
    if (!map || !onToggleNames) return;

    const handleZoomEnd = () => {
      onToggleNames(map.getZoom() >= 15);
    };

    map.on("zoomend", handleZoomEnd);

    handleZoomEnd();

    return () => {
      map.off("zoomend", handleZoomEnd);
    };
  }, [map, onToggleNames]);

  if (!geographicAreas) return null;

  const baseLabelClasses =
    "flex justify-center items-center border rounded px-2 py-0.5 shadow-xs -translate-x-1/2 min-w-fit";

  const textClasses =
    "text-white text-xs font-medium [text-shadow:0_1px_1px_rgba(0,0,0,0.5)] whitespace-normal wrap-break-word max-w-none";

  const labelStyles = {
    default: `${baseLabelClasses} bg-black/50 border-gray-300/60`,
    safeZone: `${baseLabelClasses} bg-green-800/50 border-green-300/60`,
    geofence: `${baseLabelClasses} bg-amber-800/50 border-amber-300/60`,
  };

  return (
    <>
      {/* Reference Points */}
      {geographicAreas.reference_points?.map((point, index) => {
        if (point.center_latitude && point.center_longitude && point.radius) {
          // For circular reference points
          return (
            <React.Fragment key={`ref-point-circle-${point.id || index}`}>
              <Circle
                center={[
                  Number.parseFloat(point.center_latitude),
                  Number.parseFloat(point.center_longitude),
                ]}
                radius={point.radius}
                pathOptions={{
                  color: "#5AAACB",
                  fillColor: "#5AAACB",
                  fillOpacity: 0.3,
                }}
              />
              {showRefPointsNames && (
                <Marker
                  position={[
                    Number.parseFloat(point.center_latitude),
                    Number.parseFloat(point.center_longitude),
                  ]}
                  icon={L.divIcon({
                    className: "",
                    html: `
                      <div class="${labelStyles.default}">
                        <div class="${textClasses}">${point.name}</div>
                      </div>
                    `,
                    iconSize: [0, 0],
                    iconAnchor: [0, 0],
                  })}
                  zIndexOffset={1000}
                />
              )}
            </React.Fragment>
          );
        } else if (point.polygon_coordinates) {
          // For polygon reference points
          const coords = point.polygon_coordinates.split(";").map((coord) => {
            const [lat, lng] = coord.split(",").map(Number);
            return [lat, lng] as [number, number];
          });

          const centroid = calculatePolygonCentroid(coords);

          return (
            <React.Fragment key={`ref-point-polygon-${point.id || index}`}>
              <Polygon
                positions={coords}
                pathOptions={{
                  color: "#5AAACB",
                  fillColor: "#5AAACB",
                  fillOpacity: 0.3,
                }}
              />

              {showRefPointsNames && centroid && (
                <Marker
                  position={centroid}
                  icon={L.divIcon({
                    className: "",
                    html: `
                      <div class="${labelStyles.default}">
                        <div class="${textClasses}">${point.name}</div>
                      </div>
                    `,
                    iconSize: [0, 0],
                    iconAnchor: [0, 0],
                  })}
                  zIndexOffset={1000}
                />
              )}
            </React.Fragment>
          );
        }
        return null;
      })}

      {/* Safe Zones */}
      {geographicAreas.safe_zones?.map((zone, index) => {
        if (zone.center_coordinates) {
          const [lat, lng] = zone.center_coordinates.split(",").map(Number);
          return (
            <React.Fragment key={`safe-zone-${zone.id || index}`}>
              <Circle
                center={[lat, lng]}
                radius={zone.radius ?? 0}
                pathOptions={{
                  color: "#74f274",
                  fillColor: "#74f274",
                  fillOpacity: 0.3,
                }}
              />

              {showRefPointsNames && (
                <Marker
                  position={[lat, lng]}
                  icon={L.divIcon({
                    className: "",
                    html: `
                      <div class="${labelStyles.safeZone}">
                        <div class="${textClasses}">${zone.name}</div>
                      </div>
                    `,
                    iconSize: [0, 0],
                    iconAnchor: [0, 0],
                  })}
                  zIndexOffset={1000}
                />
              )}
            </React.Fragment>
          );
        }
        return null;
      })}

      {/* Geofences */}
      {geographicAreas.geofences?.map((fence, index) => {
        const coords = fence.coordinates.split(";").map((coord) => {
          const [lat, lng] = coord.split(",").map(Number);
          return [lat, lng] as [number, number];
        });

        const centroid = calculatePolygonCentroid(coords);

        return (
          <React.Fragment key={`geofence-${fence.id || index}`}>
            <Polygon
              positions={coords}
              pathOptions={{
                color: "#eef274",
                fillColor: "#eef274",
                fillOpacity: 0.3,
              }}
            />

            {showRefPointsNames && centroid && (
              <Marker
                position={centroid}
                icon={L.divIcon({
                  className: "",
                  html: `
                    <div class="${labelStyles.geofence}">
                      <div class="${textClasses}">${fence.title}</div>
                    </div>
                  `,
                  iconSize: [0, 0],
                  iconAnchor: [0, 0],
                })}
                zIndexOffset={1000}
              />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
});

function calculatePolygonCentroid(
  coords: [number, number][],
): [number, number] | null {
  if (!coords.length) return null;

  let x = 0;
  let y = 0;

  for (const coord of coords) {
    x += coord[0];
    y += coord[1];
  }

  return [x / coords.length, y / coords.length];
}

export default GeographicAreasLayer;
